import { graphql } from "gatsby";
import React from "react";

import heroImgPlaceholder from "images/volunteer/hero-img.png";

import Layout from "../../components/layout";
import LocationPageComponent from "../../components/location-page/locationPageComponent";
import Newsletter from "../../components/newsletter";
import Seo from "../../components/seo";
import Hero from "../../components/shared/hero";
import { ThemeContext } from "../../context/ThemeContext";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { formatHeroBackgroundOptions } from "../../utils/formatContent";

const LocationPageTemplate = ({ data }) => {
  const page = data?.page;

  const { globalConfig, acfLandingPage } = page ?? {};
  const heroOpts = formatHeroBackgroundOptions(globalConfig);

  const heroBackgroundImage = heroOpts?.image?.desktop;
  const heroBackgroundImageMobile = heroOpts?.image?.mobile;
  const heroBackgroundVideo = heroOpts?.video?.desktop;

  const heroOverlayTitle =
    acfLandingPage && acfLandingPage?.genHeaderOverlayTitle
      ? acfLandingPage?.genHeaderOverlayTitle
      : "";

  const headerIcons = (acfLandingPage && acfLandingPage?.genHeaderIcons) ?? [];

  const heroNavLinks = headerIcons?.map((icon, idx) => {
    return {
      imgPath: "location-icon.svg",
      toPage: "#",
      text: `Something something-${idx}`
    };
  });

  const secondaryLocationData =
    data?.secondaryPrograms?.geographical?.nodes ?? [];

  const locationPageConfig = {
    page,
    genericDetails: data?.genericDetails,
    secondaryLocations: secondaryLocationData,
    customPrograms:
      acfLandingPage?.genProgramLocation?.flatMap((t) => t.programs?.nodes) ??
      []
  };

  const { stickyImage } =
    data?.genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const banner = useBannerDetails(page?.slug);

  const enquireUKCardDetails = {
    footerTitle: banner?.title ?? "",
    footerSubtitle: banner?.desc ?? "",
    footerImage: banner?.image || stickyImage,
    cardBookDesc: "",
    enquireNowCtaTitle: "Enquire",
    footerCtaTitle: "Apply",
    footerSubtitleClass: "text-sm planet:text-white text-black-lighter",
    contentContainerClass: "sm:w-2/5",
    cardBookDescSpanClass:
      "text-xs font-semibold text-left sm:font-normal planet:text-white text-generic-text",
    enquireBtn: true,
    allowCookieControl: false,
    displayCloseButton: false,
    footerPromoCookieName: [
      "isPromoStickyFooterShow",
      page?.id ?? page?.slug
    ].join("")
  };

  return (
    <ThemeContext.Consumer>
      {(theme) => (
        <Layout
          pageTitle={page.title}
          containerClass={"w-full mx-auto"}
          brandingOpts={{ ...theme }}
          stickyFooterDetails={enquireUKCardDetails}
        >
          <Hero
            heroHeadLine={heroOverlayTitle}
            heroImg={heroBackgroundImage}
            navLinks={heroNavLinks ?? ""}
            videoId={heroBackgroundVideo}
            heroImageMobile={heroBackgroundImageMobile}
          />
          <LocationPageComponent {...locationPageConfig} />
          <Newsletter />
        </Layout>
      )}
    </ThemeContext.Consumer>
  );
};

export const Head = ({ data }) => {
  const page = data?.page ?? {};
  return (
    <Seo
      title={page?.seoLegacy?.title ?? page?.seo?.title ?? page.title}
      description={page?.seoLegacy?.description ?? page?.seo?.description}
      image={page?.globalConfig?.backgroundImage?.sourceUrl}
      translations={page?.translations}
      lang={page?.locale}
      meta={[
        {
          name: "robots",
          content: page?.seoLegacy?.robots ?? page?.seo?.robots
        }
      ]}
    />
  );
};

// export async function config() {
//   return ({ params }) => {
//     return {
//       defer: true
//     };
//   };
// }

export const pageQuery = graphql`
  query ($id: String!) {
    page: wpLocationPage(id: { eq: $id }) {
      id
      title
      content
      slug
      locale {
        locale
        id
      }
      seo {
        ...seoFragment
      }
      ...WpLocationPage_SeolegacyFragment
      translations {
        locale
        post_title
        id
        href
      }
      translated {
        id
        slug
        uri
        title
        locale {
          locale
        }
      }
      template {
        templateName
      }

      globalConfig {
        backgroundVideo
        backgroundVideoJw
        backgroundImage {
          ...fullwidthFeaturedHeaderImageData
        }
        backgroundImageMobile {
          ...fullwidthFeaturedHeaderImageData
        }
      }
      geographical {
        nodes {
          acfCountryExploration {
            gviExperiencesLabel
            gviExperiencesDescription
            gviExperiences {
              experienceTitle
              experienceOneliner
              experienceFeaturedImage {
                ...iconImagesData
              }
              experienceIcon {
                ...iconImagesData
              }
            }
          }
        }
      }

      acfLandingPage {
        genHasCommunityPrograms
        genHeaderOverlayTitle
        genSecondaryHeadline
        genSubtitle
        genContentTabs {
          genTabTitle
          tabContentDesc
        }
        genFeaturedGallery {
          ...smallGalleryData
        }
        genFeaturedVideos {
          genVideoUrl
        }
        genHeaderIcons {
          genBriefTextTitle
          genIcon {
            ...iconImagesData
          }
        }
        genProgramLocation {
          id
          slug
          name
          programs {
            nodes {
              ...programMiniDataFragment
            }
          }
          teamMembers {
            nodes {
              ...TeamMemberFragment
            }
          }
          posts {
            nodes {
              ...miniBlogPostsFragment
            }
          }
          locationPages {
            nodes {
              title
              slug
              uri
              geographical {
                nodes {
                  name
                  parentId
                  id
                  acfCountryExploration {
                    countryExplorationContent
                    countryExplorationTitle
                    weekendTripsLabel
                    furtherTravelsLabel
                    gviExperiencesDescription
                    gviExperiencesLabel
                    localAdventuresLabel
                    gviExperiences {
                      experienceTitle
                      experienceOneliner
                      experienceFeaturedImage {
                        ...iconImagesData
                      }
                      experienceIcon {
                        ...iconImagesData
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    genericDetails: wp {
      globalProgramConfig: gviConfigSettings {
        acfKeyMessagingIcons {
          ...AcfKeymessagingiconsFragment
        }
        acfGenericGlobalConfig {
          ...StickyWidgetFragment
        }
      }
    }
  }
`;

export default LocationPageTemplate;
